/**
 * 0.44.75 - The system's name and icon (Settings > System name and icon).
 *
 * The name is in the browser tab (public pages: "Page title - Name")
 * and the title at the top of the admin menu; the icon is the tab icon
 * and the admin menu's picture (0.44.79: one icon for both). Both
 * arrive with /api/public/settings.
 *
 * The last values are kept in the browser and applied as soon as this
 * module loads, so the tab does not show the build's default name and
 * icon for a moment on every visit.
 */
import { appPath } from './basePath';
import { useSyncExternalStore } from 'react';
import { isAdminHost } from '../utils/adminHost';

// 0.44.76 - The GT18 mark (SVG, transparent background).
export const DEFAULT_ICON = '/gt18-logo.svg';
const STORE_KEY = 'gt18:system-identity';

let state = { name: '', iconUrl: '' };
const subs = new Set();

/* 0.45.16 - The system name and icon are for the PUBLIC site only. The
 * admin panel always shows GT18 and the GT18 mark: in its menu, in its
 * browser tab title and as its tab icon. Admin mode is on for the admin
 * hostname and for /admin addresses, and AdminLayout turns it on while
 * it is shown (setAdminMode). */
let adminMode = false;
try {
  adminMode = isAdminHost()
    || (typeof window !== 'undefined' && /^\/admin(\/|$)/.test(appPath()));
} catch { /* no window */ }

/* The fixed admin panel identity. */
export const ADMIN_NAME = 'GT18';

function read() {
  try {
    const v = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
    if (v && typeof v === 'object') {
      return { name: String(v.name || ''), iconUrl: String(v.iconUrl || '') };
    }
  } catch { /* storage unavailable */ }
  return null;
}

function applyIcon(url) {
  try {
    const href = adminMode ? DEFAULT_ICON : (url || DEFAULT_ICON);
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    if (link.getAttribute('href') !== href) {
      link.setAttribute('href', href);
      link.removeAttribute('type');   // PNG, JPEG, GIF or WebP: let the browser tell
    }
  } catch { /* no document */ }
}

/* The tab title for a page: "Page title - System name"; with no system
 * name (0.44.78: the field is empty), the page title alone. */
export function tabTitle(pageTitle) {
  const name = state.name;
  const page = String(pageTitle || '').trim();
  if (!name) return page;
  return page ? `${page} - ${name}` : name;
}

export function setSystemIdentity({ name, iconUrl }) {
  const next = { name: String(name || '').trim(), iconUrl: String(iconUrl || '') };
  if (next.name === state.name && next.iconUrl === state.iconUrl) return;
  const prevName = state.name;
  state = next;
  try { localStorage.setItem(STORE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  applyIcon(next.iconUrl);
  // A tab showing only the old name follows (pages set their own titles).
  // Not in the admin panel, whose tabs never carry the system name.
  try {
    if (!adminMode && next.name && (!document.title || document.title === prevName)) document.title = next.name;
  } catch { /* no document */ }
  subs.forEach(fn => fn());
}

export function getSystemIdentity() { return state; }

/* 0.45.16 - Called by AdminLayout: on while the admin panel is shown,
 * off when it is left for a public page (the tab icon follows). */
export function setAdminMode(on) {
  const next = !!on;
  if (next === adminMode) return;
  adminMode = next;
  applyIcon(state.iconUrl);
}

/* The system icon (tab and admin menu), or the default GT18 mark. */
export function systemIcon(st = state) {
  return st.iconUrl || DEFAULT_ICON;
}


export function useSystemIdentity() {
  return useSyncExternalStore(
    (fn) => { subs.add(fn); return () => subs.delete(fn); },
    () => state,
    () => state,
  );
}

// Apply the last known values straight away (the admin panel keeps the
// GT18 mark whatever is cached).
const cached = typeof window !== 'undefined' ? read() : null;
if (!cached) applyIcon('');
if (cached) {
  state = cached;
  applyIcon(cached.iconUrl);
  try { if (cached.name && !adminMode) document.title = cached.name; } catch { /* ignore */ }
}
